import React, { useCallback, useRef } from 'react'
import LazyHydrate from 'react-lazy-hydration'
import { PaddedContainer } from '@/atoms/PaddedContainer'
import { paths } from '@/constants/paths'
import { PortraitRowCollection } from '@/molecules/PortraitRowCollection'
import { DiscoverItemInteraction } from '@/services/Analytics/ContentInteractions'
import { findTitleProjectSlug, formatTitleLikeVerticalImagePath, formatWatchPath } from '@/services/ContentCatalog'
import { useGuildMember } from '@/services/GuildUserService'
import { CatalogTitleForMap, LinkViewModel, LinksViewProps } from '@/services/RenderService'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useLocale } from '@/utils/LocaleUtil'
import { logger } from '@/utils/logging'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { LargePortraitLink } from './LargePortraitLink'

export function LargePortraitRail(props: LinksViewProps) {
  const { isGuildMember, loading } = useGuildMember()
  const { asPath } = useSafeRouter()
  const { locale } = useLocale()
  const pathWithoutQuery = asPath.split('?')[0] ?? asPath
  const normalizedPath = locale ? pathWithoutQuery.replace(`/${locale}`, '') : pathWithoutQuery
  const isWatchLanding = normalizedPath === paths.watch.index

  const testId = `large-portrait-rail-${props.id}`
  const containerPosition = props.containerPosition ?? 0

  const hoverStartTimeRef = useRef<Map<string, number>>(new Map())
  const impressionTimeoutRef = useRef<Map<string, NodeJS.Timeout>>(new Map())
  const hasTrackedImpression = useRef<Set<string>>(new Set())

  const createInteraction = useCallback(
    (link: LinkViewModel, itemIndex: number) => {
      const contentId = link.id ?? ''
      const typename = link.metadata?.__typename as string | undefined
      const imageUrl = link.imageUrl

      if (!contentId || !typename || !imageUrl) return null

      return new DiscoverItemInteraction({
        id: contentId,
        __typename: typename as
          | 'ContentSeries'
          | 'ContentMovie'
          | 'ContentSpecial'
          | 'ContentEpisode'
          | 'ContentLivestream',
        attribution: link.attribution,
      })
        .withSurface(isWatchLanding ? 'discover' : 'generic_list')
        .withPlacement('collection', containerPosition, itemIndex, 'horizontal_swimlane')
        .withImageArtwork(getCloudinaryImageUrl({ path: imageUrl }), 'portrait_display')
    },
    [containerPosition, isWatchLanding],
  )

  return (
    <LazyHydrate whenVisible>
      <PaddedContainer className="!-mt-2 overflow-x-hidden !pt-2" fluid>
        <PortraitRowCollection id={props.id} testId={testId} haltLazyLoad>
          {props.links.map((link, itemIndex) => (
            <LargePortraitLink
              key={link.name}
              link={link}
              className="mb-2 mr-4 max-h-full overflow-hidden"
              isGuildMember={isGuildMember}
              loading={loading}
              itemIndex={itemIndex}
              createInteraction={createInteraction}
              hoverStartTimeRef={hoverStartTimeRef}
              impressionTimeoutRef={impressionTimeoutRef}
              hasTrackedImpression={hasTrackedImpression}
            />
          ))}
        </PortraitRowCollection>
      </PaddedContainer>
    </LazyHydrate>
  )
}

LargePortraitRail.mapTitle = mapTitleToLink

export function mapTitleToLink(title: CatalogTitleForMap): LinkViewModel | undefined {
  const path = formatTitleLikeVerticalImagePath(title)
  const imageUrl = path && getCloudinaryImageUrl({ path, width: 294, height: 441 })
  const linkUrl = formatWatchPath(title)

  if (!imageUrl || !linkUrl) {
    logger().error(
      `A link view model cannot be created for this title because one or both of imageUrl and linkUrl could not be generated.`,
      { component: LargePortraitRail.name, title, imageUrl, linkUrl },
    )
    return
  }

  return {
    name: title.title,
    alt: title.title,
    imageUrl,
    linkUrl,
    metadata: {
      __typename: title.type,
    },
    track: {
      eventName: 'Project Tile Clicked',
      payload: {
        name: title.title,
        slug: findTitleProjectSlug(title),
        label: title.description.short,
        tileType: 'large_portrait_rail',
        imageUrl,
        linkUrl,
      },
    },
  }
}
